import { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Select, Table, message } from "antd";
import {
  closePlaceholderTab,
  downloadPdfBlob,
  fetchFaxLines,
  fetchOutboundArtifact,
  fetchOutboundFaxes,
  isPlausibleFaxNumber,
  openPdfBlob,
  openPlaceholderTab,
  previewFax,
  sendFax,
} from "../../../service/fax";
import {
  SendLayout,
  SendFormCard,
  SendFormTitle,
  FieldGroup,
  FieldLabel,
  TextInput,
  FileDropZone,
  SelectedFileRow,
  ClearFileBtn,
  PrimaryBtn,
  SendHint,
  SectionTitle,
  FaxStatusPill,
  MetaText,
} from "../FaxStyled";
import FaxMatterSelect from "./FaxMatterSelect";

const OUTBOUND_PAGE_SIZE = 10;
const CURRENCY_FORMATTER = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

const formatFaxCost = (value) =>
  Number.isInteger(value) ? CURRENCY_FORMATTER.format(value / 100) : "—";

/**
 * Send tab: upload a PDF + enter the number, plus the sender's recent
 * outbound history below (statuses update via refresh; delivery also
 * arrives as a FAX_SENT / FAX_FAILED bell notification). Documents
 * already in DMS can also be faxed from their right-click menu on the
 * matter page — this form is the ad-hoc "fax this PDF" path.
 */
const SendFaxView = () => {
  const [toNumber, setToNumber] = useState("");
  const [matterId, setMatterId] = useState("");
  const [coverTo, setCoverTo] = useState("");
  const [coverSubject, setCoverSubject] = useState("");
  const [fromNumber, setFromNumber] = useState("");
  const [faxLines, setFaxLines] = useState([]);
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const [history, setHistory] = useState({ results: [], count: 0 });
  const [historyPage, setHistoryPage] = useState(1);
  // Starts true so the mount fetch shows a spinner instead of
  // flashing antd's empty state (same as the Fax Log table).
  const [historyLoading, setHistoryLoading] = useState(true);
  const fileInputRef = useRef(null);
  // Monotonic request sequence, same guard as FaxLogView: pagination,
  // Refresh, and the post-send reload can overlap, and a slower older
  // response must never overwrite a newer one.
  const historySeqRef = useRef(0);

  const loadHistory = useCallback(async (page = 1) => {
    const seq = ++historySeqRef.current;
    setHistoryLoading(true);
    try {
      const response = await fetchOutboundFaxes({
        page,
        pageSize: OUTBOUND_PAGE_SIZE,
      });
      if (seq !== historySeqRef.current) return; // stale response
      setHistory(response.data);
      setHistoryPage(page);
    } catch {
      if (seq === historySeqRef.current) {
        message.error("Could not load your outbound fax history.");
      }
    } finally {
      if (seq === historySeqRef.current) {
        setHistoryLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    loadHistory(1);
  }, [loadHistory]);

  useEffect(() => {
    let cancelled = false;
    fetchFaxLines()
      .then((response) => {
        if (!cancelled) setFaxLines(response?.data?.results || []);
      })
      .catch(() => {
        // Dropdown quietly falls back to "Firm default" only — the
        // send itself works without an explicit line.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const acceptFile = (candidate) => {
    if (!candidate) return;
    if (!candidate.name.toLowerCase().endsWith(".pdf")) {
      message.error("Only PDF files can be faxed.");
      return;
    }
    setFile(candidate);
  };

  const handleSend = async () => {
    // The in-flight guard (not just the disabled button) is what
    // prevents a fast double-click from faxing the same PDF twice.
    if (sending || !file || !isPlausibleFaxNumber(toNumber) || !matterId)
      return;
    setSending(true);
    try {
      const response = await sendFax({
        toNumber: toNumber.trim(),
        file,
        matterId,
        coverTo: coverTo.trim(),
        coverSubject: coverSubject.trim(),
        fromNumber,
      });
      message.success(
        `Fax queued to ${response?.data?.to_number || toNumber.trim()}. ` +
          "You'll get a notification when it's delivered."
      );
      setToNumber("");
      setMatterId("");
      setCoverTo("");
      setCoverSubject("");
      // Reset the line too — same post-send state as SendFaxModal, so
      // a sticky non-default line can't silently carry into the next
      // send (Bugbot #251).
      setFromNumber("");
      setFile(null);
      loadHistory(1);
    } catch (error) {
      const status = error?.response?.status;
      const summary =
        error?.response?.data?.error?.summary ||
        `Failed to queue the fax${status ? ` (HTTP ${status})` : ""}. Please try again.`;
      message.error(summary);
    } finally {
      setSending(false);
    }
  };

  const handlePreview = async () => {
    if (previewing || !file) return;
    setPreviewing(true);
    // Opened synchronously inside the click gesture — popup blockers
    // reject tabs opened after an await (Bugbot #253).
    const tab = openPlaceholderTab();
    try {
      const response = await previewFax({
        toNumber: toNumber.trim(),
        file,
        coverTo: coverTo.trim(),
        coverSubject: coverSubject.trim(),
        fromNumber,
      });
      openPdfBlob(response.data, tab);
    } catch (error) {
      closePlaceholderTab(tab);
      const status = error?.response?.status;
      message.error(
        `Preview failed${status ? ` (HTTP ${status})` : ""}. Please try again.`
      );
    } finally {
      setPreviewing(false);
    }
  };

  // Per-row in-flight guard: rapid clicks must not stack blank tabs
  // and parallel artifact fetches (Bugbot #253 round 2).
  const artifactBusyRef = useRef(new Set());

  const handleArtifact = async (row, mode) => {
    if (artifactBusyRef.current.has(row.id)) return;
    artifactBusyRef.current.add(row.id);
    const tab = mode === "preview" ? openPlaceholderTab() : null;
    try {
      const response = await fetchOutboundArtifact(row.id);
      if (mode === "download") {
        downloadPdfBlob(response.data, row.document_name || `fax-${row.id}.pdf`);
      } else {
        openPdfBlob(response.data, tab);
      }
    } catch (error) {
      closePlaceholderTab(tab);
      const status = error?.response?.status;
      message.error(
        `Could not fetch the fax PDF${status ? ` (HTTP ${status})` : ""}.`
      );
    } finally {
      artifactBusyRef.current.delete(row.id);
    }
  };

  const columns = [
    {
      title: "Document",
      dataIndex: "document_name",
      key: "document_name",
      // Sized by content (tableLayout="auto" below); the span caps very
      // long names so they ellipsize instead of stretching the table.
      render: (value) => (
        <span
          title={value || ""}
          style={{
            display: "inline-block",
            maxWidth: 360,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            verticalAlign: "bottom",
          }}
        >
          {value || <MetaText>—</MetaText>}
        </span>
      ),
    },
    { title: "To", dataIndex: "to_number", key: "to_number", width: 150 },
    {
      title: "Matter",
      dataIndex: "matter_name",
      key: "matter_name",
      width: 200,
      ellipsis: true,
      render: (value) => value || <MetaText>—</MetaText>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 110,
      render: (value) => <FaxStatusPill tone={value}>{value}</FaxStatusPill>,
    },
    {
      title: "Pages",
      dataIndex: "num_pages",
      key: "num_pages",
      width: 80,
      render: (value) => value ?? <MetaText>—</MetaText>,
    },
    {
      title: "Cost",
      dataIndex: "provider_cost_cents",
      key: "provider_cost_cents",
      width: 95,
      render: formatFaxCost,
    },
    {
      title: "Sent",
      dataIndex: "created_at",
      key: "created_at",
      width: 180,
      render: (value) => new Date(value).toLocaleString(),
    },
    {
      title: "Sent By",
      dataIndex: "requested_by_name",
      key: "requested_by_name",
      width: 160,
      ellipsis: true,
      render: (value) => value || <MetaText>—</MetaText>,
    },
    {
      title: "",
      key: "actions",
      width: 170,
      render: (_, row) => (
        <span style={{ display: "inline-flex", gap: 12 }}>
          <a
            data-testid={`fax-artifact-preview-${row.id}`}
            onClick={(e) => {
              e.preventDefault();
              handleArtifact(row, "preview");
            }}
            href="#preview"
          >
            Preview
          </a>
          <a
            data-testid={`fax-artifact-download-${row.id}`}
            onClick={(e) => {
              e.preventDefault();
              handleArtifact(row, "download");
            }}
            href="#download"
          >
            Download
          </a>
        </span>
      ),
    },
  ];

  return (
    <SendLayout>
      <SendFormCard>
        <SendFormTitle>Send a fax</SendFormTitle>

        <FieldGroup>
          <FieldLabel htmlFor="fax-to-number">Fax number</FieldLabel>
          <TextInput
            id="fax-to-number"
            placeholder="+1 (504) 555-1234"
            value={toNumber}
            onChange={(e) => setToNumber(e.target.value)}
            disabled={sending}
          />
        </FieldGroup>

        <FieldGroup>
          <FieldLabel htmlFor="fax-from-line">Send from</FieldLabel>
          <Select
            id="fax-from-line"
            style={{ width: "100%" }}
            value={fromNumber}
            onChange={setFromNumber}
            disabled={sending}
            options={[
              { value: "", label: "Firm default line" },
              ...faxLines.map((line) => ({
                value: line.number,
                label: `${line.label} — ${line.number}`,
              })),
            ]}
          />
        </FieldGroup>

        <FieldGroup>
          <FieldLabel>Matter (required)</FieldLabel>
          <FaxMatterSelect
            value={matterId}
            onChange={setMatterId}
            disabled={sending}
            dataTestId="fax-matter-select"
          />
        </FieldGroup>

        <FieldGroup>
          <FieldLabel htmlFor="fax-cover-to">To (cover page)</FieldLabel>
          <TextInput
            id="fax-cover-to"
            placeholder="Recipient name, e.g. Dr. Alice Chen"
            value={coverTo}
            onChange={(e) => setCoverTo(e.target.value)}
            maxLength={255}
            disabled={sending}
          />
        </FieldGroup>

        <FieldGroup>
          <FieldLabel htmlFor="fax-cover-subject">Subject (cover page)</FieldLabel>
          <TextInput
            id="fax-cover-subject"
            placeholder="e.g. Records request — Claim C-12345"
            value={coverSubject}
            onChange={(e) => setCoverSubject(e.target.value)}
            maxLength={500}
            disabled={sending}
          />
        </FieldGroup>

        <FieldGroup>
          <FieldLabel>PDF to send</FieldLabel>
          {file ? (
            <SelectedFileRow data-testid="selected-fax-file">
              <span>
                {file.name} ({Math.max(1, Math.round(file.size / 1024))} KB)
              </span>
              <ClearFileBtn
                aria-label="Remove file"
                onClick={() => setFile(null)}
                disabled={sending}
              >
                ✕
              </ClearFileBtn>
            </SelectedFileRow>
          ) : (
            <FileDropZone
              data-testid="fax-drop-zone"
              active={dragActive}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                setDragActive(true);
              }}
              onDragLeave={() => setDragActive(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragActive(false);
                acceptFile(e.dataTransfer.files?.[0]);
              }}
            >
              Drop a PDF here, or click to choose a file
            </FileDropZone>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,.pdf"
            style={{ display: "none" }}
            data-testid="fax-file-input"
            onChange={(e) => {
              acceptFile(e.target.files?.[0]);
              e.target.value = "";
            }}
          />
        </FieldGroup>

        <div style={{ display: "flex", gap: 10 }}>
          <PrimaryBtn
            onClick={handleSend}
            disabled={
              sending || !file || !isPlausibleFaxNumber(toNumber) || !matterId
            }
          >
            {sending ? "Queueing…" : "Send fax"}
          </PrimaryBtn>
          <PrimaryBtn
            data-testid="fax-preview-btn"
            // Outline style only while enabled — inline styles would
            // otherwise override the :disabled gray (Bugbot #253 r2).
            style={
              previewing || sending || !file
                ? undefined
                : {
                    background: "transparent",
                    color: "#1f3864",
                    border: "1px solid #1f3864",
                  }
            }
            onClick={handlePreview}
            disabled={previewing || sending || !file}
          >
            {previewing ? "Rendering…" : "Preview"}
          </PrimaryBtn>
        </div>

        <SendHint>
          Preview opens exactly what will be transmitted, cover page
          included, in a new tab.
          Filling in To or Subject adds a firm cover page as page one.
          Double-check the number — the document will be transmitted to
          whoever answers it. To fax a document that's already in DMS,
          right-click it on the matter page and choose "Send fax".
        </SendHint>
      </SendFormCard>

      <div>
        <SectionTitle>
          Your outbound faxes
          <PrimaryBtn
            style={{ padding: "6px 14px", fontSize: 13 }}
            onClick={() => loadHistory(historyPage)}
            disabled={historyLoading}
          >
            Refresh
          </PrimaryBtn>
        </SectionTitle>
        <Table
          // Shrink-wrap to content and let the browser auto-layout the
          // columns: fixed layout (antd's default with ellipsis) gives
          // the width-less Document column every leftover pixel.
          style={{ marginTop: 12, width: "max-content", maxWidth: "100%" }}
          tableLayout="auto"
          size="small"
          rowKey="id"
          columns={columns}
          dataSource={history.results}
          loading={historyLoading}
          pagination={{
            current: historyPage,
            pageSize: OUTBOUND_PAGE_SIZE,
            total: history.count,
            showSizeChanger: false,
            onChange: (page) => loadHistory(page),
          }}
        />
      </div>
    </SendLayout>
  );
};

SendFaxView.propTypes = {};

export default SendFaxView;
